import AWS from 'aws-sdk';

class KinesisService {
  constructor() {
    // Log environment variables for debugging
    console.log('🔧 KinesisService Environment Configuration:');
    console.log('   REACT_APP_AWS_REGION:', process.env.REACT_APP_AWS_REGION || 'eu-central-1');
    console.log('   REACT_APP_KINESIS_STREAM_NAME:', process.env.REACT_APP_KINESIS_STREAM_NAME || 'feedback-in');
    console.log('   REACT_APP_AWS_ACCESS_KEY_ID:', process.env.REACT_APP_AWS_ACCESS_KEY_ID ? '***SET***' : 'NOT SET');
    console.log('   REACT_APP_AWS_SECRET_ACCESS_KEY:', process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? '***SET***' : 'NOT SET');
    
    // Debug: Log all environment variables starting with REACT_APP_
    console.log('🔍 All REACT_APP_ environment variables:');
    Object.keys(process.env)
      .filter(key => key.startsWith('REACT_APP_'))
      .forEach(key => {
        const value = process.env[key];
        const displayValue = key.includes('SECRET') || key.includes('KEY') 
          ? (value ? '***SET***' : 'NOT SET')
          : value;
        console.log(`   ${key}:`, displayValue);
      });
    
    // Configure AWS SDK with explicit environment variable credentials
    this.kinesis = new AWS.Kinesis({
      region: process.env.REACT_APP_AWS_REGION || 'eu-central-1',
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    });
    
    this.streamName = process.env.REACT_APP_KINESIS_STREAM_NAME || 'feedback-in';
  }

  /**
   * Transform form data to match the expected Kinesis message format
   */
  transformFormDataToKinesisMessage(formData, metrics) {
    const messageId = require('uuid').v4();
    const timestamp = Date.now();
    
    // Create a comprehensive text field that includes all feedback
    const feedbackText = this.buildFeedbackText(formData);
    
    // Determine question type based on the primary content
    let questionType = 'text';
    if (formData.freeFeedback && formData.freeFeedback.trim()) {
      questionType = 'text';
    } else if (formData.overallImpression || formData.clarityOfPresentation || formData.demoRelevance) {
      questionType = 'rating';
    } else if (formData.engagement || formData.mostValuablePart) {
      questionType = 'mcq';
    }
    
    // Calculate average score from rating fields
    const scores = [
      formData.overallImpression,
      formData.clarityOfPresentation,
      formData.demoRelevance
    ].filter(score => score !== undefined && score !== null);
    
    const averageScore = scores.length > 0 
      ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
      : null;

    return {
      msgId: messageId,
      ts: timestamp,
      userHash: this.generateUserHash(),
      text: feedbackText,
      lang: formData.lang || 'en',
      questionType: questionType,
      score: averageScore,
      email: formData.email && formData.email.trim() ? formData.email.trim() : null,
      metrics: {
        inputDurationMs: metrics.inputDurationMs,
        timeSinceLastInputMs: metrics.timeSinceLastInputMs,
        totalSubmissionTimeMs: metrics.totalSubmissionTimeMs
      }
    };
  }

  /**
   * Build a comprehensive text field from all form data
   */
  buildFeedbackText(formData) {
    const parts = [];
    
    // Add ratings
    if (formData.overallImpression) {
      parts.push(`Overall Impression: ${formData.overallImpression}/5`);
    }
    if (formData.clarityOfPresentation) {
      parts.push(`Clarity: ${formData.clarityOfPresentation}/5`);
    }
    if (formData.demoRelevance) {
      parts.push(`Demo Relevance: ${formData.demoRelevance}/5`);
    }
    
    // Add MCQ responses
    if (formData.engagement) {
      parts.push(`Engagement: ${formData.engagement}`);
    }
    if (formData.mostValuablePart) {
      parts.push(`Most Valuable: ${formData.mostValuablePart}`);
    }
    
    // Add free feedback
    if (formData.freeFeedback && formData.freeFeedback.trim()) {
      parts.push(`Free Feedback: ${formData.freeFeedback.trim()}`);
    }
    
    return parts.join(' | ');
  }

  /**
   * Generate a user hash for anonymization
   */
  generateUserHash() {
    const CryptoJS = require('crypto-js');
    const fingerprint = navigator.userAgent + navigator.language + window.screen.width + window.screen.height;
    return `sha256:${CryptoJS.SHA256(fingerprint).toString()}`;
  }

  /**
   * Send a message to Kinesis
   */
  async sendMessage(message) {
    try {
      const params = {
        StreamName: this.streamName,
        Data: JSON.stringify(message),
        PartitionKey: message.userHash
      };

      const result = await this.kinesis.putRecord(params).promise();
      
      console.log('Successfully sent message to Kinesis:', {
        messageId: message.msgId,
        sequenceNumber: result.SequenceNumber,
        shardId: result.ShardId
      });
      
      return {
        success: true,
        sequenceNumber: result.SequenceNumber,
        shardId: result.ShardId
      };
    } catch (error) {
      console.error('Error sending message to Kinesis:', error);
      throw new Error(`Failed to send message to Kinesis: ${error.message}`);
    }
  }

  /**
   * Send form data to Kinesis
   */
  async sendFormData(formData, metrics) {
    try {
      const message = this.transformFormDataToKinesisMessage(formData, metrics);
      return await this.sendMessage(message);
    } catch (error) {
      console.error('Error processing form data for Kinesis:', error);
      throw error;
    }
  }

  /**
   * Verify Kinesis connection and stream
   */
  async verifyConnection() {
    try {
      const result = await this.kinesis.describeStream({ StreamName: this.streamName }).promise();
      return {
        success: true,
        streamStatus: result.StreamDescription.StreamStatus,
        shardCount: result.StreamDescription.Shards.length
      };
    } catch (error) {
      console.error('Error verifying Kinesis connection:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

const kinesisService = new KinesisService();
export default kinesisService;
